import * as THREE from 'three'
import {
  cloneGSplatClipPacket,
  type GSplatClipPacket,
} from '@/plugins/gsplat/clipping/GSplatClipState'
// 导入色彩空间相关类型
import { GammaMode, ToneMappingMode } from '@/plugins/gsplat/core/ColorSpaceConstants'
import { GSPLAT_INSTANCE_SIZE } from '@/plugins/gsplat/core/gsplatBatching'
import {
  type GSplatShaderEffectConfig,
  resolveGSplatShaderEffectSource,
} from '@/plugins/gsplat/effects/shaderEffectTypes'
import { assembleGSplatShaders, registerGSplatChunks } from '@/plugins/gsplat/shaders/gsplat-chunks'
import {
  createGSplatCovarianceOnlyModifyChunk,
  createGSplatModifyChunk,
  createGSplatModifyFragmentChunk,
} from '@/plugins/gsplat/shaders/gsplatModifyChunk'
// 导入 Blue Noise 纹理工具
import { createBlueNoiseTexture } from '@/plugins/gsplat/utils/BlueNoiseTexture'
import { getLoggerManager } from '@/shared/logging'
import type { CompiledGSplatWorkBufferModifier } from '@/shared/types/gsplat'

const log = getLoggerManager().getLogger('gsplat:material')

// 确保 chunk 表已注册，组装 GLSL 源码时会展开 #include
registerGSplatChunks()

/**
 * 渲染模式枚举
 * 用于切换不同的可视化/调试模式
 */
export enum GSplatRenderMode {
  /** 默认高斯渲染 */
  DEFAULT = 0,
  /** 点云模式 */
  POINTS = 1,
  /** 平面椭圆模式 */
  FLAT = 2,
  /** 深度热力图 */
  DEPTH = 3,
  /** 透明度热力图 */
  OPACITY = 4,
  /** 缩放热力图 */
  SCALE = 5,
  /** 法线可视化 */
  NORMAL = 6,
}

/**
 * 高斯点云材质配置选项
 * 与 PlayCanvas 完全对照
 */
export interface GaussianSplatMaterialOptions {
  /** 是否启用深度写入 (默认: false) */
  depthWrite?: boolean | undefined
  /** 是否启用深度测试 (默认: true) */
  depthTest?: boolean | undefined
  /** 是否透明 (默认: true) */
  transparent?: boolean | undefined
  /** 是否启用球谐光照 (默认: true) */
  enableSH?: boolean | undefined
  /** 最大球谐阶数 0-3 (默认: 3) */
  maxSHBands?: number | undefined
  /** 是否启用对数深度缓存 (默认: false) - 用于大场景渲染 */
  useLogDepth?: boolean
  /** 是否使用半精度浮点 (默认: false) - 用于移动端优化 */
  useHalfPrecision?: boolean
  /** 是否启用 Clipping Planes 支持 (默认: false) */
  clipping?: boolean
  /** 是否启用抗锯齿补偿 (默认: true) - PlayCanvas GSPLAT_AA 功能 */
  enableAA?: boolean
  /** Dithering 模式 (默认: 'none') - PlayCanvas DITHER_* 功能 */
  dither?: 'none' | 'bayer8' | 'bluenoise'
  /** 是否启用 WebGPU 模式 (默认: false) - WebGL2-only 构建中会被忽略 */
  webgpu?: boolean
  /** 渲染通道类型；pick / shadow / prepass 默认不继承最终渲染 shader effect。 */
  pass?: 'default' | 'pick' | 'shadow' | 'prepass'
  /** alpha 裁剪阈值 (用于 pick/shadow/prepass) */
  alphaClip?: number
  /** 最小屏幕投影尺寸（像素），低于该值的 splat 会被剔除。 */
  minPixelSize?: number
  /** 是否启用 Overdraw 可视化 (默认: false) - 用于调试过绘制问题 */
  enableOverdraw?: boolean

  // ============================================================
  // 色彩空间相关选项（对标PlayCanvas）
  // ============================================================

  /** Gamma校正模式（默认: 'auto' 自动检测） */
  gammaMode?: GammaMode | 'auto'
  /** Tone mapping模式（默认: LINEAR） */
  toneMapping?: ToneMappingMode
  /** 曝光值（默认: 1.0） */
  exposure?: number
}

/**
 * 高斯点云着色器材质
 * 与 PlayCanvas gsplat-material.js 完全对照
 *
 * 特点：
 * 1. 使用纹理存储 Splat 数据（位置、旋转、缩放、颜色）
 * 2. 支持基于相机视角的动态排序
 * 3. 使用预乘 Alpha 混合模式
 * 4. 支持球谐光照（SH）用于视角相关的颜色变化
 * 5. 支持 Dithering、Pick Pass、Shadow Pass、Prepass
 */
export class GaussianSplatMaterial extends THREE.ShaderMaterial {
  // ============================================================
  // 私有状态（色彩空间管理）
  // ============================================================

  /** 当前Gamma模式 */
  private _gammaMode: GammaMode | 'auto' = 'auto'
  /** 当前Tone Mapping模式 */
  private _toneMapping: ToneMappingMode = ToneMappingMode.NONE
  private _shaderEffect: GSplatShaderEffectConfig | null = null
  private _shaderEffectActive = false
  private _workBufferModifier: CompiledGSplatWorkBufferModifier | null = null
  private _clipPacket: GSplatClipPacket | null = null
  private _ownsClipTexture = false
  private _shaderEffectUniformBackup: Map<string, THREE.IUniform | undefined> = new Map()
  private _shaderEffectDefineBackup: Map<string, unknown> = new Map()
  /** 材质私有蓝噪声纹理，避免模块级纹理反向保活多个 Renderer。 */
  private _blueNoiseTexture: THREE.DataTexture | null = null

  /**
   * 构造函数
   * @param options 材质配置选项
   */
  constructor(options: GaussianSplatMaterialOptions = {}) {
    const {
      depthWrite = false, // 关键：必须禁用深度写入，否则后面的 splat 会被剔除
      depthTest = true,
      transparent = true,
      enableSH = true,
      maxSHBands = 3,
      useLogDepth = false,
      useHalfPrecision = false,
      clipping = false,
      enableAA = false,
      dither = 'none',
      webgpu = false,
      pass = 'default',
      alphaClip = 0.3, // 修复：与 PlayCanvas 保持一致（原为 0.5）
      minPixelSize = 2.0,
      enableOverdraw = false,
      // 色彩空间选项
      gammaMode = 'auto',
      toneMapping = ToneMappingMode.NONE, // 修复：与 PlayCanvas 默认值一致
      exposure = 1.0,
    } = options

    if (webgpu) {
      log.warn('[GaussianSplatMaterial] WebGPU 已禁用（WebGL2-only），将忽略 webgpu 配置')
    }

    const { vertexShader, fragmentShader } = assembleGSplatShaders()

    super({
      vertexShader,
      fragmentShader,
      glslVersion: THREE.GLSL3,

      // Uniforms - 与 PlayCanvas 命名一致
      uniforms: {
        // 视口参数
        viewport: { value: new THREE.Vector2(1920, 1080) },
        viewport_size: { value: new THREE.Vector4(1920, 1080, 1 / 1920, 1 / 1080) },

        // 相机参数：[1/far, far, near, isOrtho]
        camera_params: { value: new THREE.Vector4(0.01, 100, 0.1, 0) },

        // RTE：相机世界位置（用于大坐标场景精度优化）
        // 使用高低位分解提高精度：high = float32(pos), low = pos - high
        cameraPositionHigh: { value: new THREE.Vector3() },
        cameraPositionLow: { value: new THREE.Vector3() },

        // RTE：sceneOrigin 到相机的向量（CPU 预计算双精度差值，避免 GPU 大数相减精度损失）
        originToCameraHigh: { value: new THREE.Vector3() },
        originToCameraLow: { value: new THREE.Vector3() },

        // 数据纹理 - PlayCanvas 命名
        transformA: { value: null }, // 位置 + 旋转前半部分
        transformB: { value: null }, // 旋转后半部分 + 缩放
        splatColor: { value: null }, // 颜色 + 不透明度 (PlayCanvas: splatColor)
        splatOrder: { value: null }, // 排序索引 (PlayCanvas: splatOrder)

        // 元数据
        numSplats: { value: 0 },
        dataWidth: { value: 0 },
        orderWidth: { value: 0 },

        // 渲染模式和调试
        renderMode: { value: GSplatRenderMode.DEFAULT },
        depthRange: { value: new THREE.Vector2(0.1, 100) },

        // SH 相关 - 对标 PlayCanvas: 使用 usampler2D + 11-10-11 位打包
        // 三套 SH 管线命名约定：
        //   1. PLY 标准 SH：splatSH_1to3 ~ splatSH_12to15，数据来自 PLY 文件
        //   2. SOGS 调色板 SH：packedShN + sogsSH_labels + sogsSHN_mins/maxs，
        //      数据来自 SOGS 压缩格式的调色板查找
        //   3. 高质量 SH 预计算：sh_result + sh_labels，
        //      数据来自 GSplatResolveSH 的 GPU 预计算结果
        // 注意：packedShN 在 PLY 和 SOGS 管线中语义不同（PLY 不使用）
        splatSH_1to3: { value: null },
        splatSH_4to7: { value: null },
        splatSH_8to11: { value: null },
        splatSH_12to15: { value: null },
        shBands: { value: 0 },

        // SOGS 压缩格式专用纹理和参数
        splatMeansLow: { value: null },
        splatMeansHigh: { value: null },
        splatQuats: { value: null },
        splatScales: { value: null },
        splatSH0: { value: null },

        // SOGS 元数据
        sogsMeans_mins: { value: new THREE.Vector3() },
        sogsMeans_maxs: { value: new THREE.Vector3() },
        sogsScales_mins: { value: new THREE.Vector3() },
        sogsScales_maxs: { value: new THREE.Vector3() },
        sogsSH0_mins: { value: new THREE.Vector4() },
        sogsSH0_maxs: { value: new THREE.Vector4() },

        // SOGS V2 Codebook 纹理
        sogsScalesCodebook: { value: null },
        sogsSH0Codebook: { value: null },

        // SOGS SH 纹理（非高质量模式，用于标准 SH 评估路径）
        // 命名约定：
        // - sogsSH_labels: SOGS 原始/调色板 SH 评估路径
        // - sh_labels: 高质量 SH 预计算查表路径
        packedShN: { value: null }, // SH 系数调色板纹理
        sogsSH_labels: { value: null }, // SH 标签纹理
        sogsSHN_mins: { value: 0.0 }, // SH 归一化最小值
        sogsSHN_maxs: { value: 0.0 }, // SH 归一化最大值

        // 高质量 SH 解析纹理（来自 GSplatResolveSH）
        sh_result: { value: null },
        sh_labels: { value: null },
        shN_mins: { value: new THREE.Vector4() },
        shN_maxs: { value: new THREE.Vector4() },

        // Pick/Shadow/Prepass 参数
        pickId: { value: new THREE.Vector4(0, 0, 0, 1) },
        pickMinDistanceEnabled: { value: false },
        pickMinDistance: { value: 0 },
        pickRayOriginView: { value: new THREE.Vector3() },
        pickRayDirectionView: { value: new THREE.Vector3(0, 0, -1) },
        alphaClip: { value: alphaClip },
        minPixelSize: { value: minPixelSize },

        // Blue Noise 纹理（用于 DITHER_BLUENOISE 模式）
        blueNoiseTexture: { value: null },

        // 时间抖动偏移（每帧由 CPU 更新，用于 Dithering）
        blueNoiseJitter: { value: new THREE.Vector2(0, 0) },

        // 色彩空间参数
        exposure: { value: exposure },

        // 颜色调节参数（对标 SuperSplat）
        colorBrightness: { value: 0.0 }, // 亮度 (-1 ~ 1)
        colorTemperature: { value: 0.0 }, // 色温 (-1 ~ 1)
        colorContrast: { value: 1.0 }, // 对比度 (0 ~ 2, 默认 1 为中性值)
        colorSaturation: { value: 1.0 }, // 饱和度 (0 ~ 2, 默认 1 为中性值)
        colorOpacity: { value: 1.0 }, // 不透明度 (0 ~ 1)

        // 标量过滤参数
        splatScalar: { value: null }, // 标量纹理 (R32F)
        scalarFilterEnabled: { value: false }, // 是否启用标量过滤
        scalarFilterRange: { value: new THREE.Vector2(0, 1) }, // 过滤范围 [min, max]

        // unified LOD polygon 中心点裁切
        gsplatClipData: { value: null },
        gsplatClipPolygonCount: { value: 0 },
      },

      // 渲染状态
      transparent,
      depthWrite,
      depthTest,

      // 3DGS 专用混合模式（预乘 Alpha）
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor,
      blendDstAlpha: THREE.OneMinusSrcAlphaFactor,

      // 双面渲染
      side: THREE.DoubleSide,
    })

    // 设置 shader defines
    this.defines = this.defines || {}
    this.defines.GSPLAT_INSTANCE_SIZE = GSPLAT_INSTANCE_SIZE

    // SH 支持
    if (enableSH && maxSHBands > 0) {
      this.defines.SH_BANDS = Math.min(maxSHBands, 3)
    }

    // 对数深度缓存支持
    if (useLogDepth) {
      this.defines.USE_LOGDEPTHBUF = ''
      this.defines.USE_LOGDEPTHBUF_EXT = ''
    }

    // 半精度浮点支持
    if (useHalfPrecision) {
      this.defines.USE_HALF_PRECISION = ''
    }

    // 抗锯齿补偿（PlayCanvas GSPLAT_AA）
    if (enableAA) {
      this.defines.GSPLAT_AA = 1
    }

    // Overdraw 可视化模式
    if (enableOverdraw) {
      this.defines.GSPLAT_OVERDRAW = 1
      // Overdraw 需要加法混合
      this.blending = THREE.AdditiveBlending
      this.depthWrite = false
      this.transparent = true
    }

    // Dithering 模式 (PlayCanvas 兼容)
    // 注意：Dither 模式启用 depthWrite 并禁用混合，实现完全不透明渲染
    if (dither === 'none') {
      this.defines.DITHER_NONE = 1
    } else if (dither === 'bayer8') {
      this.defines.DITHER_BAYER8 = 1
      this.depthWrite = true // Dither 模式需要深度写入
      this.blending = THREE.NoBlending // 修复：禁用混合（与 PlayCanvas 一致）
      this.transparent = false // 修复：关闭透明标志
    } else if (dither === 'bluenoise') {
      this.defines.DITHER_BLUENOISE = 1
      this.depthWrite = true // Dither 模式需要深度写入
      this.blending = THREE.NoBlending // 修复：禁用混合（与 PlayCanvas 一致）
      this.transparent = false // 修复：关闭透明标志
      // 每个材质独占纹理；共享模块级 Texture 会累计旧 Renderer 的 dispose listener。
      this._blueNoiseTexture = createBlueNoiseTexture()
      this.uniforms.blueNoiseTexture.value = this._blueNoiseTexture
    }

    // 渲染通道
    if (pass === 'pick') {
      this.defines.PICK_PASS = 1
    } else if (pass === 'shadow') {
      this.defines.SHADOW_PASS = 1
    } else if (pass === 'prepass') {
      this.defines.PREPASS_PASS = 1
    }

    if (pass === 'pick' || pass === 'shadow' || pass === 'prepass') {
      // 这些 pass 需要写入精确结果，不能被透明混合或 tone mapping 污染。
      this.blending = THREE.NoBlending
      this.transparent = false
      this.toneMapped = false
      this.depthWrite = true
    }

    // Clipping Planes 支持
    this.clipping = clipping

    // 保存色彩空间配置
    this._gammaMode = gammaMode
    this._toneMapping = toneMapping

    // 应用色彩空间defines
    this._updateGammaDefines()
    this._updateToneMappingDefines()

    // 标记为 GaussianSplatMaterial (使用 name 而非只读的 type)
    this.name = 'GaussianSplatMaterial'
  }

  /**
   * 设置 PlayCanvas 风格的 3DGS 最终渲染 shader effect。
   *
   * direct / SOGS 通常是独立材质作用域；unified LOD 若共享材质，则 effect 会按共享材质生效。
   * pick / shadow / prepass 使用独立材质，默认不复制该最终渲染 effect。
   *
   * @param effect - effect 配置；传 null 时清除当前 effect
   */
  setShaderEffect(effect: GSplatShaderEffectConfig | null): void {
    if (effect) {
      // WebGL material mutation 之前先验证当前语言；WGSL-only effect 不得静默降级为 no-op。
      resolveGSplatShaderEffectSource(effect, 'glsl', 'vertex')
      resolveGSplatShaderEffectSource(effect, 'glsl', 'fragment')
    }

    this._clearShaderEffectState()
    this._shaderEffect = effect
    this._shaderEffectActive = effect !== null

    if (effect) {
      this._applyShaderEffectDefines(effect)
      this._applyShaderEffectUniforms(effect)
    }

    this._rebuildShaderEffectSource()
  }

  /**
   * 清除当前材质上的 3DGS shader effect，并恢复默认 no-op hook。
   *
   * @param expectedEffect - 可选的所有者配置；与当前挂载配置不同时不执行清理
   */
  clearShaderEffect(expectedEffect?: GSplatShaderEffectConfig): void {
    if (expectedEffect && this._shaderEffect !== expectedEffect) {
      return
    }
    this.setShaderEffect(null)
  }

  /** 将 effect 标记为逻辑完成，同时保留已编译 shader 并恢复基础 uniform。 */
  deactivateShaderEffect(effect: GSplatShaderEffectConfig): boolean {
    if (this._shaderEffect !== effect) {
      return false
    }

    for (const [name, uniform] of this._shaderEffectUniformBackup) {
      const effectUniform = this.uniforms[name]
      if (uniform !== undefined && effectUniform) {
        effectUniform.value = uniform.value
      }
    }
    this._shaderEffectActive = false
    return true
  }

  /**
   * 获取当前正在生效的 3DGS shader effect 配置。
   *
   * @returns 当前 effect 配置；没有启用时返回 null
   */
  getShaderEffect(): GSplatShaderEffectConfig | null {
    return this._shaderEffectActive ? this._shaderEffect : null
  }

  setWorkBufferModifier(modifier: CompiledGSplatWorkBufferModifier | null): void {
    if (
      this._workBufferModifier?.hash === modifier?.hash &&
      this._workBufferModifier?.code === modifier?.code
    ) {
      return
    }

    this._workBufferModifier = modifier
    this._rebuildShaderEffectSource()
  }

  /**
   * 设置 unified LOD polygon 裁切 packet。
   *
   * @param packet - 编译后的裁切纹理；null 表示清除
   * @param ownsTexture - 当前材质是否负责释放 packet 纹理
   */
  setClipPacket(packet: GSplatClipPacket | null, ownsTexture = false): void {
    const previousTexture = this._clipPacket?.texture ?? null
    const nextTexture = packet?.texture ?? null
    const wasEnabled = this._clipPacket !== null
    const isEnabled = packet !== null

    if (this._ownsClipTexture && previousTexture && previousTexture !== nextTexture) {
      previousTexture.dispose()
    }

    this._clipPacket = packet
    this._ownsClipTexture = packet !== null && ownsTexture
    this.uniforms.gsplatClipData.value = nextTexture
    this.uniforms.gsplatClipPolygonCount.value = packet
      ? (packet.volumeCount ?? packet.polygonCount)
      : 0

    if (isEnabled) {
      this.defines.GSPLAT_CLIP_ENABLED = 1
    } else {
      Reflect.deleteProperty(this.defines, 'GSPLAT_CLIP_ENABLED')
    }

    if (wasEnabled !== isEnabled) {
      this.needsUpdate = true
    }
  }

  /** 清除 polygon 裁切并释放当前材质拥有的纹理。 */
  clearClipPacket(): void {
    this.setClipPacket(null)
  }

  /** 返回当前裁切 packet，供 pick material 借用。 */
  getClipPacket(): GSplatClipPacket | null {
    return this._clipPacket
  }

  /** 从源材质借用裁切状态，不取得纹理所有权。 */
  copyClipStateFrom(source: GaussianSplatMaterial): void {
    this.setClipPacket(source.getClipPacket(), false)
  }

  /**
   * 只更新 effect uniform，不触发 shader 重新编译。
   *
   * @param name - uniform 名称
   * @param value - 新 uniform 值
   * @returns 找到并更新 uniform 时返回 true
   */
  setShaderEffectUniform(name: string, value: unknown): boolean {
    const uniform = this.uniforms[name]
    if (uniform) {
      if (this._shaderEffect && !this._shaderEffectUniformBackup.has(name)) {
        this._shaderEffectUniformBackup.set(name, { ...uniform })
      }
      uniform.value = value
      return true
    }

    if (!this._shaderEffect) {
      return false
    }

    if (!this._shaderEffectUniformBackup.has(name)) {
      this._shaderEffectUniformBackup.set(name, undefined)
    }
    this.uniforms[name] = { value }
    return true
  }

  /**
   * 更新视口尺寸。
   *
   * @param width - 视口宽度（像素）
   * @param height - 视口高度（像素）
   */
  setViewport(width: number, height: number): void {
    this.uniforms.viewport.value.set(width, height)
    this.uniforms.viewport_size.value.set(width, height, 1 / width, 1 / height)
  }

  /**
   * 更新相机参数（PlayCanvas 兼容）。
   *
   * @param camera - 当前相机，支持透视与正交
   */
  setCameraParams(camera: THREE.Camera): void {
    if (camera instanceof THREE.PerspectiveCamera) {
      this.uniforms.camera_params.value.set(1 / camera.far, camera.far, camera.near, 0)
    } else if (camera instanceof THREE.OrthographicCamera) {
      this.uniforms.camera_params.value.set(1 / camera.far, camera.far, camera.near, 1)
    }
  }

  /**
   * 设置相机世界位置（用于 RTE 渲染，解决大坐标精度问题）。
   *
   * 使用高低位分解：high = float32(pos)，low = pos - high，GPU 端可以精确计算相对位置。
   *
   * @param position - 相机世界坐标
   */
  setCameraPositionWorld(position: THREE.Vector3): void {
    // 高位：直接转换为 float32（JavaScript Number -> float32）
    const highX = Math.fround(position.x)
    const highY = Math.fround(position.y)
    const highZ = Math.fround(position.z)

    // 低位：原始值与高位的差值（保留精度）
    const lowX = position.x - highX
    const lowY = position.y - highY
    const lowZ = position.z - highZ

    this.uniforms.cameraPositionHigh.value.set(highX, highY, highZ)
    this.uniforms.cameraPositionLow.value.set(lowX, lowY, lowZ)
  }

  /**
   * 设置 sceneOrigin 到相机的向量（用于 WorkBuffer 模式的大坐标精度优化）。
   *
   * CPU 端预计算双精度差值，避免 GPU 中两个大数相减导致的精度跳变。
   *
   * @param sceneOrigin - 场景原点（世界坐标）
   * @param cameraPosition - 相机位置（世界坐标）
   */
  setOriginToCamera(sceneOrigin: THREE.Vector3, cameraPosition: THREE.Vector3): void {
    // 双精度减法（JavaScript Number 是 float64）
    const diffX = sceneOrigin.x - cameraPosition.x
    const diffY = sceneOrigin.y - cameraPosition.y
    const diffZ = sceneOrigin.z - cameraPosition.z

    // 对差值做高低位分解
    const highX = Math.fround(diffX)
    const highY = Math.fround(diffY)
    const highZ = Math.fround(diffZ)

    this.uniforms.originToCameraHigh.value.set(highX, highY, highZ)
    this.uniforms.originToCameraLow.value.set(diffX - highX, diffY - highY, diffZ - highZ)
  }

  /**
   * 设置数据纹理 - PlayCanvas 命名。
   *
   * @param transformA - 位置与旋转前半纹理
   * @param transformB - 旋转后半与缩放纹理
   * @param splatColor - 颜色 + 不透明度纹理
   * @param splatOrder - 排序索引纹理
   */
  setDataTextures(
    transformA: THREE.DataTexture,
    transformB: THREE.DataTexture,
    splatColor: THREE.DataTexture,
    splatOrder: THREE.DataTexture
  ): void {
    this.uniforms.transformA.value = transformA
    this.uniforms.transformB.value = transformB
    this.uniforms.splatColor.value = splatColor
    this.uniforms.splatOrder.value = splatOrder
  }

  /**
   * 设置统一LOD纹理（从 WorkBuffer）
   *
   * 用于将 GSplatManager 的 WorkBuffer 纹理绑定到材质。
   * WorkBuffer 包含了 LOD 筛选后的 Splat 数据。
   *
   * @param colorTexture 颜色纹理（RGBA）
   * @param splatTexture0 几何纹理0（位置+旋转前半部分）
   * @param splatTexture1 几何纹理1（旋转后半部分+缩放）
   * @param orderTexture 排序索引纹理
   * @param textureSize 纹理尺寸（宽度=高度）
   */
  setUnifiedLODTextures(
    colorTexture: THREE.DataTexture,
    splatTexture0: THREE.DataTexture,
    splatTexture1: THREE.DataTexture,
    orderTexture: THREE.DataTexture,
    textureSize: number
  ): void {
    // 绑定纹理到对应的 uniforms
    // WorkBuffer 输出格式（与标准 PLY 格式不同）：
    // - colorTexture (RGBA16F 或 RGBA8 fallback) -> splatColor
    // - splatTexture0 (RGBA32UI) -> transformA: worldCenter.xyz + pack(covA.z, covB.z)
    // - splatTexture1 (RG32UI) -> transformB: pack(covA.xy) + pack(covB.xy)
    // - orderTexture (R32U) -> splatOrder
    this.uniforms.splatColor.value = colorTexture
    this.uniforms.transformA.value = splatTexture0
    this.uniforms.transformB.value = splatTexture1
    this.uniforms.splatOrder.value = orderTexture

    // 更新纹理尺寸（dataWidth 和 orderWidth 使用相同的尺寸）
    this.uniforms.dataWidth.value = textureSize
    this.uniforms.orderWidth.value = textureSize

    let needsUpdate = false

    // 启用 WorkBuffer 数据模式（使用预计算的世界空间协方差）
    if (!this.defines.GSPLAT_WORKBUFFER_DATA) {
      this.defines.GSPLAT_WORKBUFFER_DATA = 1
      needsUpdate = true
    }

    // 保留资源自带的 SH_BANDS（WorkBuffer 颜色可能包含 SH0/SHN），仅在缺纹理时关闭以避免采样器未绑定
    const hasSHTextures =
      !!this.uniforms.splatSH_1to3.value ||
      !!this.uniforms.splatSH_4to7.value ||
      !!this.uniforms.splatSH_8to11.value ||
      !!this.uniforms.splatSH_12to15.value
    if (!hasSHTextures && this.defines.SH_BANDS !== 0) {
      this.defines.SH_BANDS = 0
      this.uniforms.shBands.value = 0
      this.uniforms.splatSH_1to3.value = null
      this.uniforms.splatSH_4to7.value = null
      this.uniforms.splatSH_8to11.value = null
      this.uniforms.splatSH_12to15.value = null
      needsUpdate = true
    }

    if (needsUpdate) {
      this.needsUpdate = true
    }
  }

  /**
   * 设置元数据。
   *
   * @param numSplats - 当前可见 splat 数量
   * @param dataWidth - 数据纹理宽度（像素）
   * @param orderWidth - 排序纹理宽度（像素）
   */
  setMetadata(numSplats: number, dataWidth: number, orderWidth: number): void {
    this.uniforms.numSplats.value = numSplats
    this.uniforms.dataWidth.value = dataWidth
    this.uniforms.orderWidth.value = orderWidth
  }

  /**
   * 设置渲染模式
   * 用于切换不同的可视化/调试模式
   *
   * @param mode 渲染模式（见 GSplatRenderMode 枚举）
   *
   * @example
   * ```typescript
   * // 切换到色彩空间调试模式
   * material.setRenderMode(GSplatRenderMode.DEBUG_RAW_COLOR);
   *
   * // 恢复默认渲染
   * material.setRenderMode(GSplatRenderMode.DEFAULT);
   * ```
   */
  setRenderMode(mode: GSplatRenderMode | number): void {
    this.uniforms.renderMode.value = mode

    const needsDebugVaryings =
      mode === GSplatRenderMode.DEPTH ||
      mode === GSplatRenderMode.SCALE ||
      mode === GSplatRenderMode.NORMAL
    const hasDebugVaryings = 'GSPLAT_DEBUG_VARYINGS' in this.defines

    if (needsDebugVaryings && !hasDebugVaryings) {
      this.defines.GSPLAT_DEBUG_VARYINGS = 1
      this.needsUpdate = true
    } else if (!needsDebugVaryings && hasDebugVaryings) {
      delete this.defines.GSPLAT_DEBUG_VARYINGS
      this.needsUpdate = true
    }
  }

  /**
   * 获取当前渲染模式。
   *
   * @returns 当前绑定的 GSplatRenderMode
   */
  getRenderMode(): GSplatRenderMode {
    return this.uniforms.renderMode.value as GSplatRenderMode
  }

  /**
   * 获取渲染模式的描述信息，用于调试输出。
   *
   * @param mode - 渲染模式枚举
   * @returns 对应中文描述；未知模式返回带编号的占位字符串
   */
  static getRenderModeDescription(mode: GSplatRenderMode): string {
    const descriptions: Record<number, string> = {
      [GSplatRenderMode.DEFAULT]: '默认高斯渲染',
      [GSplatRenderMode.POINTS]: '点云模式',
      [GSplatRenderMode.FLAT]: '平面椭圆模式',
      [GSplatRenderMode.DEPTH]: '深度热力图',
      [GSplatRenderMode.OPACITY]: '透明度热力图',
      [GSplatRenderMode.SCALE]: '缩放热力图',
      [GSplatRenderMode.NORMAL]: '法线可视化',
    }
    return descriptions[mode] || `未知模式 (${mode})`
  }

  /**
   * 设置球谐（SH）纹理和阶数
   * 对标 PlayCanvas：使用 RGBA32UI 位打包格式
   *
   * @param shTextures SH 纹理数组 [sh1to3, sh4to7?, sh8to11?, sh12to15?]
   * @param shBands SH 阶数 (1-3)
   */
  setSHTextures(shTextures: THREE.DataTexture[], shBands: number): void {
    this.uniforms.shBands.value = Math.min(shBands, 3)

    if (shBands > 0) {
      this.defines = this.defines || {}
      this.defines.SH_BANDS = Math.min(shBands, 3)
      this.needsUpdate = true
    }

    // 设置纹理（对标 PlayCanvas 布局）
    this.uniforms.splatSH_1to3.value = shTextures.length > 0 ? shTextures[0] : null
    this.uniforms.splatSH_4to7.value = shTextures.length > 1 ? shTextures[1] : null
    this.uniforms.splatSH_8to11.value = shTextures.length > 2 ? shTextures[2] : null
    this.uniforms.splatSH_12to15.value = shTextures.length > 3 ? shTextures[3] : null
  }

  /**
   * 设置 alpha 裁剪阈值。
   *
   * @param value - 阈值，取值 0~1；低于该值的像素被丢弃
   */
  setAlphaClip(value: number): void {
    this.uniforms.alphaClip.value = value
  }

  setMinPixelSize(value: number): void {
    const uniformName = 'minPixelSize'
    if (this._shaderEffectUniformBackup.has(uniformName)) {
      const backup = this._shaderEffectUniformBackup.get(uniformName)
      if (backup) {
        backup.value = value
      } else {
        this._shaderEffectUniformBackup.set(uniformName, { value })
      }
    }

    this.uniforms.minPixelSize.value = value
  }

  /**
   * 更新时间抖动偏移（每帧调用）
   * 用于 Dithering 模式，减少静态抖动图案
   */
  updateDitherJitter(): void {
    this.uniforms.blueNoiseJitter.value.set(Math.random(), Math.random())
  }

  /**
   * 设置 Pick ID，用于拾取通道。
   *
   * @param id - 拾取 ID，Vector4 或长度 4 的数组；缺失分量按 0 填充
   */
  setPickId(id: THREE.Vector4 | number[]): void {
    if (id instanceof THREE.Vector4) {
      this.uniforms.pickId.value.copy(id)
    } else if (Array.isArray(id)) {
      this.uniforms.pickId.value.set(id[0] ?? 0, id[1] ?? 0, id[2] ?? 0, id[3] ?? 0)
    }
  }

  /**
   * 设置 pick pass 的最小射线距离裁剪。
   *
   * 该参数只影响 pick material，不改变普通渲染或 GPU snapshot 的提交时序。
   */
  setPickMinDistance(
    camera: THREE.Camera,
    ray: THREE.Ray | undefined,
    minDistance: number | undefined
  ): void {
    const enabled = minDistance !== undefined && ray !== undefined
    this.uniforms.pickMinDistanceEnabled.value = enabled
    this.uniforms.pickMinDistance.value = minDistance ?? 0
    if (!enabled || !ray) {
      this.uniforms.pickRayOriginView.value.set(0, 0, 0)
      this.uniforms.pickRayDirectionView.value.set(0, 0, -1)
      return
    }

    camera.updateMatrixWorld(true)
    const cameraPosition = camera.getWorldPosition(new THREE.Vector3())
    const inverseCameraRotation = camera.getWorldQuaternion(new THREE.Quaternion()).invert()
    this.uniforms.pickRayOriginView.value
      .copy(ray.origin)
      .sub(cameraPosition)
      .applyQuaternion(inverseCameraRotation)
    this.uniforms.pickRayDirectionView.value
      .copy(ray.direction)
      .normalize()
      .applyQuaternion(inverseCameraRotation)
      .normalize()
  }

  /**
   * 设置 SOGS 数据纹理和元数据
   * @param textures SOGS 纹理对象
   * @param meta SOGS 元数据
   */
  setSogsTextures(
    textures: {
      meansLow: THREE.Texture
      meansHigh: THREE.Texture
      quats: THREE.Texture
      scales: THREE.Texture
      sh0: THREE.Texture
      scalesCodebook?: THREE.DataTexture
      sh0Codebook?: THREE.DataTexture
    },
    meta: {
      version: 1 | 2
      means: { mins: [number, number, number]; maxs: [number, number, number] }
      scales: {
        mins?: [number, number, number]
        maxs?: [number, number, number]
        codebook?: Array<number | null | undefined>
      }
      sh0: {
        mins?: [number, number, number, number]
        maxs?: [number, number, number, number]
        codebook?: Array<number | null | undefined>
      }
    }
  ): void {
    // 设置纹理
    this.uniforms.splatMeansLow.value = textures.meansLow
    this.uniforms.splatMeansHigh.value = textures.meansHigh
    this.uniforms.splatQuats.value = textures.quats
    this.uniforms.splatScales.value = textures.scales
    this.uniforms.splatSH0.value = textures.sh0

    // 设置元数据
    this.uniforms.sogsMeans_mins.value.set(...meta.means.mins)
    this.uniforms.sogsMeans_maxs.value.set(...meta.means.maxs)

    // 设置 defines
    this.defines = this.defines || {}
    this.defines.GSPLAT_SOGS_DATA = 1

    if (meta.version === 2) {
      // V2: Codebook 模式
      this.defines.GSPLAT_SOGS_V2 = 1
      if (textures.scalesCodebook) {
        this.uniforms.sogsScalesCodebook.value = textures.scalesCodebook
      }
      if (textures.sh0Codebook) {
        this.uniforms.sogsSH0Codebook.value = textures.sh0Codebook
      }
    } else {
      // V1: Min-Max 模式
      delete this.defines.GSPLAT_SOGS_V2
      if (meta.scales.mins && meta.scales.maxs) {
        this.uniforms.sogsScales_mins.value.set(...meta.scales.mins)
        this.uniforms.sogsScales_maxs.value.set(...meta.scales.maxs)
      }
      if (meta.sh0.mins && meta.sh0.maxs) {
        this.uniforms.sogsSH0_mins.value.set(...meta.sh0.mins)
        this.uniforms.sogsSH0_maxs.value.set(...meta.sh0.maxs)
      }
    }

    this.needsUpdate = true
  }

  /**
   * 设置 SOGS SH 纹理（标准 SH 评估路径）
   *
   * 当 SOGS 数据包含高阶 SH 系数时使用此方法设置纹理。
   * 这与 setHighQualitySHTexture 不同：
   * - setHighQualitySHTexture: 用于预计算的 SH 查找（性能优化）
   * - setSogsSHTextures: 用于标准的逐顶点 SH 评估（完整质量）
   *
   * @param packedShN SH 系数调色板纹理（SH_COEFFS*64 x N, RGBA8）
   * @param shLabels SH 标签纹理（splat -> palette index, RG8）
   * @param shN_mins SH 归一化最小值
   * @param shN_maxs SH 归一化最大值
   * @param shBands SH 阶数 (1-3)
   */
  setSogsSHTextures(
    packedShN: THREE.Texture | null,
    shLabels: THREE.Texture | null,
    shN_mins: number,
    shN_maxs: number,
    shBands: number
  ): void {
    this.uniforms.packedShN.value = packedShN
    this.uniforms.sogsSH_labels.value = shLabels
    this.uniforms.sogsSHN_mins.value = shN_mins
    this.uniforms.sogsSHN_maxs.value = shN_maxs

    if (packedShN && shLabels && shBands > 0) {
      // 启用 SH 支持
      this.defines.SH_BANDS = Math.min(shBands, 3)
      this.uniforms.shBands.value = Math.min(shBands, 3)
    } else {
      // 禁用 SH 支持
      delete this.defines.SH_BANDS
      this.uniforms.shBands.value = 0
    }

    this.needsUpdate = true
  }

  /**
   * 设置高质量 SH 纹理（来自 GSplatResolveSH）
   *
   * 当启用高质量 SH 时，将使用预计算的 SH 查找纹理替代每顶点 SH 评估，
   * 可以显著提升性能（10-30%）。
   *
   * @param shResultTexture SH 结果纹理（64x1024 RGBA8）或 null（禁用）
   * @param shLabelsTexture SH 标签纹理（splat -> palette index）
   * @param shN_mins SH 归一化最小值
   * @param shN_maxs SH 归一化最大值
   */
  setHighQualitySHTexture(
    shResultTexture: THREE.Texture | null,
    shLabelsTexture?: THREE.Texture | null,
    shN_mins?: number,
    shN_maxs?: number
  ): void {
    const nextLabels =
      shLabelsTexture !== undefined ? shLabelsTexture : this.uniforms.sh_labels.value
    const shouldEnable = shResultTexture !== null && nextLabels !== null
    const wasEnabled = 'USE_HIGH_QUALITY_SH' in this.defines

    this.uniforms.sh_result.value = shResultTexture

    if (shLabelsTexture !== undefined) {
      this.uniforms.sh_labels.value = shouldEnable ? shLabelsTexture : null
    } else if (!shouldEnable) {
      this.uniforms.sh_labels.value = null
    }
    if (shN_mins !== undefined) {
      this.uniforms.shN_mins.value.setScalar(shN_mins)
    }
    if (shN_maxs !== undefined) {
      this.uniforms.shN_maxs.value.setScalar(shN_maxs)
    }

    if (shouldEnable && !wasEnabled) {
      this.defines.USE_HIGH_QUALITY_SH = 1
      this.needsUpdate = true
    } else if (!shouldEnable && wasEnabled) {
      delete this.defines.USE_HIGH_QUALITY_SH
      this.needsUpdate = true
    }
  }

  /**
   * 释放资源
   */
  private _rebuildShaderEffectSource(): void {
    const chunkOverrides = this._workBufferModifier
      ? { gsplatModifyVS: createGSplatCovarianceOnlyModifyChunk(this._workBufferModifier.code) }
      : this._shaderEffect
        ? {
            gsplatModifyVS: createGSplatModifyChunk(this._shaderEffect.glsl),
            gsplatModifyPS: createGSplatModifyFragmentChunk(this._shaderEffect.fragmentGLSL),
          }
        : undefined
    const { vertexShader, fragmentShader } = assembleGSplatShaders({ chunkOverrides })

    this.vertexShader = vertexShader
    this.fragmentShader = fragmentShader
    this.needsUpdate = true
  }

  private _applyShaderEffectUniforms(effect: GSplatShaderEffectConfig): void {
    if (!effect.uniforms) {
      return
    }

    for (const [name, uniform] of Object.entries(effect.uniforms)) {
      if (!this._shaderEffectUniformBackup.has(name)) {
        this._shaderEffectUniformBackup.set(name, this.uniforms[name])
      }
      this.uniforms[name] = uniform
    }
  }

  private _applyShaderEffectDefines(effect: GSplatShaderEffectConfig): void {
    if (!effect.defines) {
      return
    }

    this.defines = this.defines || {}
    for (const [name, value] of Object.entries(effect.defines)) {
      if (!this._shaderEffectDefineBackup.has(name)) {
        this._shaderEffectDefineBackup.set(name, this.defines[name])
      }
      this.defines[name] = value
    }
  }

  private _clearShaderEffectState(): void {
    this._shaderEffect?.dispose?.()
    this._shaderEffect = null
    this._shaderEffectActive = false

    for (const [name, uniform] of this._shaderEffectUniformBackup) {
      if (uniform === undefined) {
        delete this.uniforms[name]
      } else {
        this.uniforms[name] = uniform
      }
    }
    this._shaderEffectUniformBackup.clear()

    for (const [name, value] of this._shaderEffectDefineBackup) {
      if (value === undefined) {
        delete this.defines[name]
      } else {
        this.defines[name] = value
      }
    }
    this._shaderEffectDefineBackup.clear()
  }

  dispose(): void {
    this._clearShaderEffectState()
    this.clearClipPacket()
    this._blueNoiseTexture?.dispose()
    this._blueNoiseTexture = null
    this.uniforms.blueNoiseTexture.value = null
    super.dispose()
    this.uniforms.transformA.value = null
    this.uniforms.transformB.value = null
    this.uniforms.splatColor.value = null
    this.uniforms.splatOrder.value = null
    this.uniforms.splatSH_1to3.value = null
    this.uniforms.splatSH_4to7.value = null
    this.uniforms.splatSH_8to11.value = null
    this.uniforms.splatSH_12to15.value = null
    // SOGS 纹理
    this.uniforms.splatMeansLow.value = null
    this.uniforms.splatMeansHigh.value = null
    this.uniforms.splatQuats.value = null
    this.uniforms.splatScales.value = null
    this.uniforms.splatSH0.value = null
    this.uniforms.sogsScalesCodebook.value = null
    this.uniforms.sogsSH0Codebook.value = null
    // SOGS SH 纹理
    this.uniforms.packedShN.value = null
    this.uniforms.sogsSH_labels.value = null
    // 高质量 SH 纹理
    this.uniforms.sh_result.value = null
    this.uniforms.sh_labels.value = null
  }

  // ============================================================
  // 色彩空间管理方法
  // ============================================================

  /**
   * 设置Gamma模式
   * @param mode Gamma校正模式
   */
  setGammaMode(mode: GammaMode): void {
    if (this._gammaMode === mode) return

    this._gammaMode = mode
    this._updateGammaDefines()
    this.needsUpdate = true
  }

  /**
   * 获取当前Gamma模式
   * @returns 当前Gamma模式
   */
  getGammaMode(): GammaMode | 'auto' {
    return this._gammaMode
  }

  /**
   * 设置Tone Mapping模式
   * @param mode Tone mapping模式
   */
  setToneMapping(mode: ToneMappingMode): void {
    if (this._toneMapping === mode) return

    this._toneMapping = mode
    this._updateToneMappingDefines()
    this.needsUpdate = true
  }

  /**
   * 获取当前Tone Mapping模式
   * @returns 当前Tone mapping模式
   */
  getToneMapping(): ToneMappingMode {
    return this._toneMapping
  }

  /**
   * 设置曝光值
   * @param value 曝光值（默认1.0）
   */
  setExposure(value: number): void {
    this.uniforms.exposure.value = value
  }

  /**
   * 获取曝光值
   * @returns 当前曝光值
   */
  getExposure(): number {
    return this.uniforms.exposure.value
  }

  // ============================================================
  // 颜色调节 API（对标 SuperSplat）
  // ============================================================

  /**
   * 批量设置颜色调节参数
   * @param options 颜色调节选项
   */
  setColorAdjustment(options: {
    brightness?: number // 亮度 (-1 ~ 1, 默认 0)
    temperature?: number // 色温 (-1 ~ 1, 默认 0)
    contrast?: number // 对比度 (0 ~ 2, 默认 1)
    saturation?: number // 饱和度 (0 ~ 2, 默认 1)
    opacity?: number // 不透明度 (0 ~ 1, 默认 1)
  }): void {
    if (options.brightness !== undefined) {
      this.uniforms.colorBrightness.value = Math.max(-1, Math.min(1, options.brightness))
    }
    if (options.temperature !== undefined) {
      this.uniforms.colorTemperature.value = Math.max(-1, Math.min(1, options.temperature))
    }
    if (options.contrast !== undefined) {
      this.uniforms.colorContrast.value = Math.max(0, Math.min(2, options.contrast))
    }
    if (options.saturation !== undefined) {
      this.uniforms.colorSaturation.value = Math.max(0, Math.min(2, options.saturation))
    }
    if (options.opacity !== undefined) {
      this.uniforms.colorOpacity.value = Math.max(0, Math.min(1, options.opacity))
    }
  }

  /**
   * 获取所有颜色调节参数
   * @returns 当前颜色调节参数
   */
  getColorAdjustment(): {
    brightness: number
    temperature: number
    contrast: number
    saturation: number
    opacity: number
  } {
    return {
      brightness: this.uniforms.colorBrightness.value,
      temperature: this.uniforms.colorTemperature.value,
      contrast: this.uniforms.colorContrast.value,
      saturation: this.uniforms.colorSaturation.value,
      opacity: this.uniforms.colorOpacity.value,
    }
  }

  /**
   * 重置所有颜色调节参数为默认值
   */
  resetColorAdjustment(): void {
    this.uniforms.colorBrightness.value = 0.0
    this.uniforms.colorTemperature.value = 0.0
    this.uniforms.colorContrast.value = 1.0
    this.uniforms.colorSaturation.value = 1.0
    this.uniforms.colorOpacity.value = 1.0
  }

  // ============================================================
  // 标量过滤 API
  // ============================================================

  /**
   * 设置标量纹理。texture 非 null 时添加 HAS_SCALAR_FILTER define，null 时移除。
   *
   * @param texture - 标量纹理；传 null 关闭过滤
   */
  setScalarTexture(texture: THREE.Texture | null): void {
    const hadDefine = this.defines && 'HAS_SCALAR_FILTER' in this.defines
    const needDefine = texture !== null

    this.uniforms.splatScalar.value = texture

    if (needDefine && !hadDefine) {
      this.defines = this.defines || {}
      this.defines.HAS_SCALAR_FILTER = 1
      this.needsUpdate = true
    } else if (!needDefine && hadDefine) {
      delete this.defines.HAS_SCALAR_FILTER
      this.needsUpdate = true
    }
  }

  /**
   * 设置标量过滤参数（仅更新 uniforms，不触发 shader 重编译）。
   *
   * @param enabled - 是否启用过滤
   * @param min - 阈值下界
   * @param max - 阈值上界
   */
  setScalarFilter(enabled: boolean, min: number, max: number): void {
    this.uniforms.scalarFilterEnabled.value = enabled
    this.uniforms.scalarFilterRange.value.set(min, max)
  }

  /**
   * 清除标量过滤
   */
  clearScalarFilter(): void {
    this.setScalarTexture(null)
    this.uniforms.scalarFilterEnabled.value = false
    this.uniforms.scalarFilterRange.value.set(0, 1)
  }

  /**
   * 设置亮度
   * @param value 亮度值 (-1 ~ 1, 默认 0)
   */
  setBrightness(value: number): void {
    this.uniforms.colorBrightness.value = Math.max(-1, Math.min(1, value))
  }

  /**
   * 获取亮度。
   *
   * @returns 当前亮度值，范围 -1 ~ 1
   */
  getBrightness(): number {
    return this.uniforms.colorBrightness.value
  }

  /**
   * 设置色温
   * @param value 色温值 (-1 ~ 1, 默认 0，正值偏暖/红，负值偏冷/蓝)
   */
  setTemperature(value: number): void {
    this.uniforms.colorTemperature.value = Math.max(-1, Math.min(1, value))
  }

  /**
   * 获取色温。
   *
   * @returns 当前色温值，范围 -1 ~ 1
   */
  getTemperature(): number {
    return this.uniforms.colorTemperature.value
  }

  /**
   * 设置对比度
   * @param value 对比度值 (0 ~ 2, 默认 1)
   */
  setContrast(value: number): void {
    this.uniforms.colorContrast.value = Math.max(0, Math.min(2, value))
  }

  /**
   * 获取对比度。
   *
   * @returns 当前对比度值，范围 0 ~ 2
   */
  getContrast(): number {
    return this.uniforms.colorContrast.value
  }

  /**
   * 设置饱和度
   * @param value 饱和度值 (0 ~ 2, 默认 1，0=灰度，>1=过饱和)
   */
  setSaturation(value: number): void {
    this.uniforms.colorSaturation.value = Math.max(0, Math.min(2, value))
  }

  /**
   * 获取饱和度。
   *
   * @returns 当前饱和度值，范围 0 ~ 2
   */
  getSaturation(): number {
    return this.uniforms.colorSaturation.value
  }

  /**
   * 设置不透明度
   * @param value 不透明度值 (0 ~ 1, 默认 1)
   */
  setOpacity(value: number): void {
    this.uniforms.colorOpacity.value = Math.max(0, Math.min(1, value))
  }

  /**
   * 获取不透明度。
   *
   * @returns 当前不透明度值，范围 0 ~ 1
   */
  getOpacity(): number {
    return this.uniforms.colorOpacity.value
  }

  /**
   * 根据渲染器自动更新gamma模式
   * 在onBeforeRender中调用（仅当gammaMode='auto'时生效）
   *
   * 【重要说明】
   * 我们使用自定义 ShaderMaterial，Three.js 不会自动注入 linearToOutputTexel()。
   * 因此当输出目标需要 sRGB 时，shader 必须自己编码（GAMMA_SRGB）。
   *
   * @param renderer Three.js WebGL渲染器
   */
  updateGammaMode(renderer: THREE.WebGLRenderer): void {
    if (this._gammaMode !== 'auto') return

    // 当输出目标需要 sRGB 时，shader 必须自己编码
    // （因为 Three.js 不会为自定义 ShaderMaterial 做颜色空间转换）
    const needsShaderGamma = renderer.outputColorSpace === THREE.SRGBColorSpace

    // define 使用空字符串作为合法值，必须检查键是否存在，避免 auto 模式每帧误判并重编译材质。
    const currentlyHasGammaSRGB = Object.hasOwn(this.defines, 'GAMMA_SRGB')
    const shouldHaveGammaSRGB = needsShaderGamma

    if (currentlyHasGammaSRGB !== shouldHaveGammaSRGB) {
      if (shouldHaveGammaSRGB) {
        delete this.defines.GAMMA_NONE
        this.defines.GAMMA_SRGB = ''
      } else {
        delete this.defines.GAMMA_SRGB
        this.defines.GAMMA_NONE = ''
      }
      this.needsUpdate = true
    }
  }

  /**
   * 更新Gamma相关defines（私有）
   */
  private _updateGammaDefines(): void {
    // 清除所有gamma defines
    delete this.defines.GAMMA_NONE
    delete this.defines.GAMMA_SRGB

    if (this._gammaMode === 'auto') {
      // auto模式暂时设置GAMMA_NONE，等运行时检测
      this.defines.GAMMA_NONE = ''
    } else if (this._gammaMode === GammaMode.SRGB) {
      this.defines.GAMMA_SRGB = ''
    } else {
      this.defines.GAMMA_NONE = ''
    }
  }

  /**
   * 更新Tone Mapping相关defines（私有）
   */
  private _updateToneMappingDefines(): void {
    // 清除所有tonemapping defines
    delete this.defines.TONEMAP_NONE
    delete this.defines.TONEMAP_LINEAR
    delete this.defines.TONEMAP_ACES
    delete this.defines.TONEMAP_FILMIC
    delete this.defines.TONEMAP_NEUTRAL

    // 设置新的define
    this.defines[`TONEMAP_${this._toneMapping}`] = ''
  }

  /**
   * 克隆材质，保留主要构造参数与渲染模式。
   *
   * @returns 新建的同类型材质实例
   */
  clone(): this {
    const material = new GaussianSplatMaterial({
      depthWrite: this.depthWrite,
      depthTest: this.depthTest,
      transparent: this.transparent,
      enableSH: !!this.defines?.SH_BANDS,
      maxSHBands: typeof this.defines?.SH_BANDS === 'number' ? this.defines.SH_BANDS : 0,
      useLogDepth: Object.hasOwn(this.defines, 'USE_LOGDEPTHBUF'),
      useHalfPrecision: Object.hasOwn(this.defines, 'USE_HALF_PRECISION'),
      clipping: this.clipping,
      enableAA: !!this.defines?.GSPLAT_AA,
      dither: this.defines?.DITHER_BAYER8
        ? 'bayer8'
        : this.defines?.DITHER_BLUENOISE
          ? 'bluenoise'
          : 'none',
      pass: this.defines?.PICK_PASS
        ? 'pick'
        : this.defines?.SHADOW_PASS
          ? 'shadow'
          : this.defines?.PREPASS_PASS
            ? 'prepass'
            : 'default',
      alphaClip: this.uniforms.alphaClip.value,
      minPixelSize: this.uniforms.minPixelSize.value,
      enableOverdraw: !!this.defines?.GSPLAT_OVERDRAW,
      // 色彩空间选项
      gammaMode: this._gammaMode,
      toneMapping: this._toneMapping,
      exposure: this.uniforms.exposure.value,
    })

    // 复制 uniforms
    material.uniforms.viewport.value.copy(this.uniforms.viewport.value)
    material.uniforms.viewport_size.value.copy(this.uniforms.viewport_size.value)
    material.uniforms.camera_params.value.copy(this.uniforms.camera_params.value)
    material.uniforms.cameraPositionHigh.value.copy(this.uniforms.cameraPositionHigh.value)
    material.uniforms.cameraPositionLow.value.copy(this.uniforms.cameraPositionLow.value)
    material.uniforms.originToCameraHigh.value.copy(this.uniforms.originToCameraHigh.value)
    material.uniforms.originToCameraLow.value.copy(this.uniforms.originToCameraLow.value)
    material.uniforms.transformA.value = this.uniforms.transformA.value
    material.uniforms.transformB.value = this.uniforms.transformB.value
    material.uniforms.splatColor.value = this.uniforms.splatColor.value
    material.uniforms.splatOrder.value = this.uniforms.splatOrder.value
    material.uniforms.numSplats.value = this.uniforms.numSplats.value
    material.uniforms.dataWidth.value = this.uniforms.dataWidth.value
    material.uniforms.orderWidth.value = this.uniforms.orderWidth.value
    material.uniforms.splatSH_1to3.value = this.uniforms.splatSH_1to3.value
    material.uniforms.splatSH_4to7.value = this.uniforms.splatSH_4to7.value
    material.uniforms.splatSH_8to11.value = this.uniforms.splatSH_8to11.value
    material.uniforms.splatSH_12to15.value = this.uniforms.splatSH_12to15.value
    material.uniforms.shBands.value = this.uniforms.shBands.value
    if (this._clipPacket) {
      material.setClipPacket(cloneGSplatClipPacket(this._clipPacket), true)
    }

    return material as this
  }
}
